import type { ReactElement } from 'react';
import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';

import { isOffsite, isPopover } from '@hotjar/feedback/domain/Surveys/Survey/SurveyLocations';
import { Device, Widget } from '../constants';
import type { SurveyDisplayType, FeedbackDisplayType } from '../constants';

const wrapperRadius = tokens.radius.m;

const Wrapper = styled.div<{
  position?: string;
  previewDevice: Device;
  restrictWidth: boolean;
  showFade: boolean;
  widget: Widget;
}>`
  min-width: 200px;
  height: auto;
  border-radius: ${wrapperRadius};
  border: 1px solid ${tokens.color.neutral_200};
  ${tokens.elevation(1)};
  line-height: 10px;
  margin: 0 auto;
  position: relative;

  ${({ restrictWidth }) => restrictWidth && `max-width: 460px;`}

  ${({ showFade }) =>
    showFade &&
    `
    &::before {
      content: '';
      display: block;
      position: absolute;
      top: -8px;
      bottom: -8px;
      width: 100px;
      background: linear-gradient(
        to right,
        ${tokens.color.neutral_100} 0%,
        ${tokens.color.neutral_100} 17%,
        /* safari gradient transparency interpolation bug means that we need to use a transparent version of the color we are interpolating from */
        ${tokens.color.neutral_100}00 100%
      );
    }
  `}

  ${({ position }) =>
    position === 'right' &&
    `
      text-align: right;

      &::before {
          left: -8px;
      }
    `}
  ${({ position }) =>
    position === 'left' &&
    `
      text-align: left;

      &::before {
          right: -8px;
          transform: rotate(180deg);
      }
    `}

    ${({ previewDevice, widget }) =>
    previewDevice === Device.PHONE &&
    widget === Widget.SURVEY &&
    `
      max-width: 320px;
    `}
    ${({ previewDevice, widget }) =>
    previewDevice === Device.PHONE &&
    widget === Widget.FEEDBACK &&
    `
      max-width: 390px;
    `}
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  height: 35px;
  padding: 0 12px 0 12px;
  background: ${tokens.color.neutral_200};
  border-bottom: 1px solid ${tokens.color.neutral_200};
  text-align: left;
`;

const Dots = styled.span`
  line-height: 0;

  & > span {
    width: 8px;
    height: 8px;
    display: inline-block;
    border-radius: 50%;
    background: ${tokens.color.neutral_400};
    margin-right: 6px;
  }
`;

const BurgerIcon = styled.span`
  align-self: flex-end;
  width: 16px;
  height: 20px;
  border-radius: 0;
  background: transparent;
  line-height: 0;
  margin: 0;

  & > span {
    display: inline-block;
    width: 100%;
    height: 3px;
    background: ${tokens.color.neutral_400};
    border-radius: 5px;
  }
`;

const Viewport = styled.div<{
  background?: string;
  isOffsiteSurvey: boolean;
  previewDevice: Device;
}>`
  // Prevent survey modal overlay from leaking into the border radius.
  overflow: hidden;
  // Viewport radius needs to be 1px less than the Wrapper radius to look good.
  --viewport-radius: calc(${wrapperRadius} - 1px);

  ${({ previewDevice }) =>
    previewDevice === Device.PHONE
      ? `
        border-radius: var(--viewport-radius);
      `
      : `
        border-radius: 0 0 var(--viewport-radius) var(--viewport-radius);
      `}

  ${({ isOffsiteSurvey, background }) =>
    isOffsiteSurvey
      ? `
          background: ${background || tokens.color.white};
          padding: 20px;
        `
      : `
          background: ${tokens.color.white};
        `}
`;

interface BrowserFrameProps {
  backgroundColor?: string;
  children: ReactElement[];
  displayType?: SurveyDisplayType | FeedbackDisplayType;
  position: string;
  previewDevice: Device;
  widget: Widget;
}

export const BrowserFrame = ({
  children,
  backgroundColor,
  displayType,
  position = '',
  previewDevice,
  widget,
}: BrowserFrameProps): JSX.Element => {
  const adjustedPosition = position.endsWith('left') ? 'left' : 'right';
  const isFeedback = widget === Widget.FEEDBACK;
  const isSurvey = widget === Widget.SURVEY;
  const isOffsiteSurvey = isSurvey && !!displayType && isOffsite(displayType as SurveyDisplayType);
  const isPopoverSurvey = isSurvey && !!displayType && isPopover(displayType as SurveyDisplayType);
  const widgetPosition = isFeedback || isPopoverSurvey ? adjustedPosition : undefined;
  const showFade = previewDevice === Device.DESKTOP && (isFeedback || isPopoverSurvey);

  return (
    <Wrapper
      position={widgetPosition}
      previewDevice={previewDevice}
      restrictWidth={isFeedback || isPopoverSurvey}
      showFade={showFade}
      widget={widget}
    >
      {previewDevice === Device.DESKTOP && (
        <Header>
          {adjustedPosition === 'left' && (
            <Dots data-testid="browser-frame-dots">
              <span />
              <span />
              <span />
            </Dots>
          )}
          {adjustedPosition === 'right' && (
            <BurgerIcon data-testid="browser-frame-burger">
              <span />
              <span />
              <span />
            </BurgerIcon>
          )}
        </Header>
      )}
      <Viewport
        background={backgroundColor}
        isOffsiteSurvey={isOffsiteSurvey}
        previewDevice={previewDevice}
      >
        {children}
      </Viewport>
    </Wrapper>
  );
};
